<template>
  <div v-if="data">
    <BackgroundImage />
    <CategoryHero
      :adopter-breadcrumb="data.adopterBreadcrumb"
      :breadcrumbs="data.breadcrumb"
      :title="data.title"
      :title-long="data.titleLong"
      :description="data.description"
      :subcategories="data.subcategories"
      :header-image="data.headerImage ? { src: data.headerImage } : null"
    />
    <div ref="top" class="relative">
      <FilterBar
        ref="filterBar"
        :filters="data.filters"
        :applied-filters="filters"
        :order-by-options="orderByOptions"
        :default-sort-order="defaultSortOrder"
        :order-by="effectiveOrderBy"
        @order-by-changed="setOrderBy"
        @update-choices="(group: number, choices: number[]) => updateChoices(group, choices)"
        @update-range="(group: number, min: number, max: number) => updateRange(group, min, max)"
      />
      <FilterMenu
        :filters="data.filters"
        :applied-filters="filters"
        :order-by-options="orderByOptions"
        :default-sort-order="defaultSortOrder"
        :order-by="effectiveOrderBy"
        @order-by-changed="setOrderBy"
        @filter-clear="clearFilters"
        @update-choices="(group: number, choices: number[]) => updateChoices(group, choices)"
        @update-range="(group: number, min: number, max: number) => updateRange(group, min, max)"
      />
      <AppliedFiltersList :filters="data.filters" :applied-filters="filters" @filter-remove="removeFilter" @filter-clear="clearFilters" />

      <div v-if="data.products.length" class="h-container overflow-hidden">
        <ProductGrid :products="data.products" :value-propositions="currentPage === 1 ? data.valuePropositions : undefined" />
      </div>

      <div v-if="!data.products.length" class="h-container">
        <NoResults class="my-10" :title="noResultsData.title" :suggestions="noResultsData.suggestions" :bottom-text="noResultsData.bottomText" />
      </div>

      <Pagination v-if="data.products.length" param="slk" :current-page="currentPage" :pages="data.pages" :scroll-to-top-element="elementTop" />
    </div>

    <!--NewsletterModal :image="newsletterModalImage" /-->
    <CtaBanner class="mt-8" />

    <template v-if="data.content && data.content.length > 0">
      <template v-for="(contentPiece, index) in data.content">
        <ContentModule v-if="contentPiece.type === 'html'" :key="index" class="my-20 [&_*]:scroll-mt-2">
          <DynamicContent :nodes="contentPiece.content" />
        </ContentModule>
      </template>
    </template>

    <HallFaq v-if="data.faqItems" :items="data.faqItems" class="mb-20" />

    <LastViewedProductsSection class="h-container flex flex-col items-center py-10" />
  </div>
</template>

<script setup lang="ts">
import BackgroundImage from '~/components/background-image/BackgroundImage.vue'
import FilterBar from '~/components/filter-bar/FilterBar.vue'
import FilterMenu from '~/components/filter-bar/FilterMenu.vue'
import CategoryHero from '~/components/category-hero/CategoryHero.vue'
import HallFaq from '~/components/hall/HallFaq.vue'
import AppliedFiltersList from '~/components/hall/filter/AppliedFiltersList.vue'
import Pagination from '~/components/pagination/Pagination.vue'
import CtaBanner from '~/components/cta-banner/CtaBanner.vue'
import NoResults from '~/components/no-results/NoResults.vue'
import ContentModule from '~/components/content-module/ContentModule.vue'
import DynamicContent from '~/components/utility/DynamicContent.vue'

import { useBreadcrumb } from '~/composables/structured-data'
import LastViewedProductsSection from '~/components/last-viewed-products-section/LastViewedProductsSection.vue'
import { useNoHallResults } from '~/composables/no-results'
import * as t from '~/messages'
import { useHallQuery } from '~/composables/queries/hall'

const orderByOptions: Record<string, string> = {
  '0': t['w.saal.reasta.populaarsus'](),
  '1': t['w.saal.reasta.odavamast'](),
  '2': t['w.saal.reasta.kallimast'](),
  '3': t['w.saal.reasta.soodus'](),
  '4': t['w.saal.reasta.uudsus'](),
  '8': t['w.saal.reasta.tarne'](),
}

const {
  public: { countryId },
} = useRuntimeConfig()

const route = useRoute()
const elementTop = useTemplateRef('top')
const filterBar = useTemplateRef('filterBar')

const defaultSortOrder = '0'

const { filters, filterParameters, currentPage, setOrderBy, effectiveOrderBy, removeFilter, clearFilters, updateChoices, updateRange } = useHallRefinements({
  sortOrderParameter: 'so',
  defaultSortOrder,
  orderByOptions,
  pageParameter: 'slk',
  filterBar,
  scrollToTopElement: elementTop,
})

const adopter = computed(() => (typeof route.query.adopter === 'string' ? route.query.adopter : undefined))

const { data, error, suspense } = await useHallQuery(route.path, currentPage, effectiveOrderBy, filterParameters, adopter)
await suspense()
if (error.value) {
  if (error.value.statusCode === 400 && error.value.data?.responseType === 'RedirectResponse') {
    await navigateTo(
      {
        path: error.value.data.location,
        query: route.query,
        hash: route.hash,
      },
      {
        replace: true,
      }
    )
  } else {
    throw createError(error.value)
  }
} else if (data.value?.responseType !== 'HallResponse') {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not Found',
  })
}

provideTranslationUrl(() => data.value?.translationUrl)

const noResultsData = useNoHallResults()

// const newsletterModalImage = useNewsletterState().value?.modalImage

const orderByCheapest = t['w.saal.reasta.odavamast']()
const orderByMostExpensive = t['w.saal.reasta.kallimast']()
const metaPageTitle = t['meta.title.page']()

useHead({
  title: () => {
    let title = data.value?.pageTitle
    if (!title) {
      return null
    }

    if (effectiveOrderBy.value === '1') {
      title += ` - ${orderByCheapest}`
    }

    if (effectiveOrderBy.value === '2') {
      title += ` - ${orderByMostExpensive}`
    }

    if (currentPage.value > 1) {
      title += ` - ${metaPageTitle}${currentPage.value}`
    }

    return title
  },
  link: () => [
    data.value?.canonicalUrl ? { rel: 'canonical', href: data.value.canonicalUrl } : undefined,
    data.value?.relPrev ? { rel: 'prev', href: data.value.relPrev } : undefined,
    data.value?.relNext ? { rel: 'next', href: data.value.relNext } : undefined,
  ],
  meta: () => [data.value?.metaDescription ? { name: 'description', content: data.value.metaDescription } : undefined],
})

useSchemaOrg([defineBreadcrumb(useBreadcrumb(data.value?.breadcrumb))])

const user = useUserState()

const { productImpressions } = useCriteo()
const criteoProductsListEvent = () => {
  if (countryId !== 2) {
    return
  }

  const email = user.value ? user.value.emailMd5 : null
  const products = data.value.products.map((product) => product.productId)

  productImpressions(email, products)
}

criteoProductsListEvent()
</script>
